import React, { memo, useMemo, useState } from "react";

const DEFAULT_PRICING = Object.freeze({
  market: "eu",
  currency: "EUR",
  basicMonthlyMinor: 6900,
});

function normalizePricing(value) {
  const market =
    String(value?.market || "")
      .trim()
      .toLowerCase() === "ua"
      ? "ua"
      : "eu";

  const fallbackAmount = market === "ua" ? 99000 : 6900;
  const rawAmount = Number(value?.basicMonthlyMinor);

  return {
    market,
    currency: market === "ua" ? "UAH" : "EUR",
    basicMonthlyMinor:
      Number.isInteger(rawAmount) && rawAmount >= 0
        ? rawAmount
        : fallbackAmount,
  };
}

function formatSubscriptionPrice(amountMinor, currency) {
  const rawMinor = Number(amountMinor);
  const safeMinor =
    Number.isFinite(rawMinor) && rawMinor >= 0
      ? Math.round(rawMinor)
      : 0;

  const amount = safeMinor / 100;

  const formattedAmount = Number.isInteger(amount)
    ? String(amount)
    : amount.toFixed(2).replace(".", ",");

  return currency === "UAH"
    ? `${formattedAmount} ₴`
    : `${formattedAmount} €`;
}

function Price({
  value,
  onChange,
  ta,
  pricing = DEFAULT_PRICING,
  className = "",
}) {
  const [flippedCards, setFlippedCards] = useState({});

  const currentPricing = useMemo(
    () => normalizePricing(pricing),
    [pricing]
  );

  const plans = useMemo(() => {
    const t = (key, fallback = "") =>
      ta?.(key, fallback) ?? fallback;

    const freePrice = formatSubscriptionPrice(
      0,
      currentPricing.currency
    );

    const basicPrice = formatSubscriptionPrice(
      currentPricing.basicMonthlyMinor,
      currentPricing.currency
    );

    return [
      {
        id: "free",
        title: t("landing.tariff.free", "FREE"),
        desc: t("landing.tariff.free-description", ""),
        price: freePrice,
        priceSuffix: t("landing.tariff", "/ month"),
        priceNotes: [],
        features: [
          t("landing.free.first", ""),
          t("landing.free.second", ""),
          t("landing.free.third", ""),
        ].filter(Boolean),
        oneTimeTitle: t(
          "landing.tariff.one-time-services",
          "One-time services"
        ),
        oneTimeDesc: t(
          "landing.tariff.free-one-time-desc",
          ""
        ),
        oneTimeServices: [
          t("landing.tariff.logo-creation", ""),
          t("landing.tariff.selling-images", ""),
        ].filter(Boolean),
      },
      {
        id: "basic",
        title: t("landing.tariff.basic", "BASIC"),
        desc: t("landing.tariff.basic-description", ""),
        price: basicPrice,
        priceSuffix: t("landing.tariff", "/ month"),
        badgeTop: t("landing.title.free", ""),
        priceNotes: [],
        features: [
          t("landing.basic.first", ""),
          t("landing.basic.second", ""),
          t("landing.basic.third", ""),
          t("landing.basic.fourth", ""),
        ].filter(Boolean),
        oneTimeTitle: t(
          "landing.tariff.one-time-services",
          "One-time services"
        ),
        oneTimeDesc: t(
          "landing.tariff.basic-one-time-desc",
          t("landing.tariff.free-one-time-desc", "")
        ),
        oneTimeServices: [
          t("landing.tariff.logo-creation", ""),
          t("landing.tariff.selling-images", ""),
          t("landing.tariff.panorama-creation", ""),
        ].filter(Boolean),
      },
      {
        id: "pro",
        title: t("landing.tariff.pro", "PRO"),
        desc: t("landing.tariff.pro-description", ""),
        price: "—",
        priceSuffix: "",
        locked: true,
        lockedLabel: t(
          "landing.tariff.in-development",
          "In development"
        ),
        lockedDesc: t(
          "landing.tariff.pro-locked-desc",
          ""
        ),
        priceNotes: [],
        features: [
          t("landing.pro.first", ""),
          t("landing.pro.second", ""),
          t("landing.pro.third", ""),
        ].filter(Boolean),
        oneTimeServices: [],
      },
    ];
  }, [ta, currentPricing]);

  const lockedIds = useMemo(() => {
    return new Set(
      plans
        .filter((plan) => plan.locked)
        .map((plan) => plan.id)
    );
  }, [plans]);

  const select = (id) => {
    if (!id || id === value || lockedIds.has(id)) return;
    onChange?.(id);
  };

  const onKeyPick = (event, id) => {
    if (lockedIds.has(id)) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      select(id);
    }
  };

  const toggleFlip = (event, id) => {
    event.preventDefault();
    event.stopPropagation();

    if (lockedIds.has(id)) return;

    setFlippedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const t = (key, fallback = "") =>
    ta?.(key, fallback) ?? fallback;

  return (
    <div className={`slider ${className}`.trim()}>
      <div className="slider__container slider__container--tarif">
        <ul className="slider__container--line">
          {plans.map((plan) => {
            const selected =
              !plan.locked && value === plan.id;

            const isFlipped =
              !!flippedCards[plan.id];

            const wrapperBorder = selected
              ? "2px solid #FFA500"
              : plan.locked
                ? "2px solid rgba(255, 255, 255, 0.06)"
                : "2px solid rgba(255, 255, 255, 0.1)";

            const faceBaseStyle = {
              gridArea: "1 / 1",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transition: "transform 0.48s ease",
              transformStyle: "preserve-3d",
              minHeight: "100%",
            };

            return (
              <li
                key={plan.id}
                className={`slider__container--img-base ${
                  plan.locked
                    ? "slider__container--img-base-locked"
                    : ""
                }`}
              >
                <div
                  className={`services__tariff ${
                    plan.locked
                      ? "services__tariff--locked"
                      : ""
                  }`}
                  role="button"
                  tabIndex={plan.locked ? -1 : 0}
                  onClick={() => select(plan.id)}
                  onKeyDown={(event) =>
                    onKeyPick(event, plan.id)
                  }
                  aria-pressed={selected}
                  aria-disabled={
                    plan.locked ? "true" : "false"
                  }
                  title={
                    plan.locked
                      ? plan.lockedLabel
                      : plan.title
                  }
                  style={{
                    cursor: plan.locked
                      ? "not-allowed"
                      : "pointer",
                    opacity: plan.locked ? 0.78 : 1,
                  }}
                >
                  <div
                    className="services__tariff-wrapper"
                    style={{
                      border: wrapperBorder,
                      position: "relative",
                      overflow: "hidden",
                      background: plan.locked
                        ? "linear-gradient(135deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015))"
                        : undefined,
                    }}
                  >
                    {plan.locked && (
                      <>
                        <div
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 3,
                            background:
                              "rgba(0, 0, 0, 0.12)",
                            pointerEvents: "none",
                          }}
                        />

                        <div
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            zIndex: 4,
                            transform:
                              "translate(-50%, -50%) rotate(-60deg)",
                            padding: "8px 18px",
                            borderRadius: 999,
                            border:
                              "1px solid rgba(255,255,255,0.16)",
                            background:
                              "rgba(255,255,255,0.055)",
                            color:
                              "rgba(255,255,255,0.56)",
                            fontSize: 32,
                            fontWeight: 900,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                            pointerEvents: "none",
                          }}
                        >
                          {plan.lockedLabel}
                        </div>
                      </>
                    )}

                    <div
                      className="services__tariff-flip-scene"
                      style={{
                        display: "grid",
                        perspective: "1200px",
                      }}
                    >
                      {/* FRONT SIDE */}
                      <div
                        className="services__tariff-face services__tariff-face--front"
                        style={{
                          ...faceBaseStyle,
                          transform: isFlipped
                            ? "rotateY(180deg)"
                            : "rotateY(0deg)",
                          pointerEvents: isFlipped
                            ? "none"
                            : "auto",
                        }}
                      >
                        <h6 className="title title__prices title__prices--max">
                          {plan.title}
                        </h6>

                        {!!plan.desc && (
                          <div className="title__block">
                            <div className="title title__description title__description--answer">
                              {plan.desc}
                            </div>
                          </div>
                        )}

                        <div className="services__prises-container">
                          <div className="services__prises-block">
                            <h4 className="title title__prices">
                              {plan.price}
                            </h4>

                            {!!plan.badgeTop && (
                              <div className="title__landing-free-wrapper">
                                <div className="title__landing-free">
                                  {plan.badgeTop}
                                </div>
                              </div>
                            )}
                          </div>

                          {!!plan.priceSuffix && (
                            <span className="text__prices-description">
                              {plan.priceSuffix}
                            </span>
                          )}
                        </div>

                        {Array.isArray(plan.priceNotes) &&
                          plan.priceNotes.length > 0 && (
                            <div
                              className="services__price-note"
                              style={{
                                marginTop: 8,
                                display: "grid",
                                gap: 5,
                              }}
                            >
                              {plan.priceNotes.map(
                                (note, index) => (
                                  <div
                                    key={index}
                                    className="text text__prices"
                                    style={{
                                      fontSize: 11,
                                      lineHeight: 1.25,
                                      opacity: 0.76,
                                    }}
                                  >
                                    {note}
                                  </div>
                                )
                              )}
                            </div>
                          )}

                        <ul className="services__list">
                          {plan.features.map(
                            (feature, index) => (
                              <li
                                key={index}
                                className="text text__prices"
                              >
                                {feature}
                              </li>
                            )
                          )}
                        </ul>

                        {!plan.locked && (
                          <div
                            className="title__attention-block"
                            onClick={(event) =>
                              toggleFlip(
                                event,
                                plan.id
                              )
                            }
                          >
                            <div className="title__attention">
                              {t(
                                "landing.tariff.optional"
                              )}
                            </div>

                            <div className="title__attention-description">
                              {t(
                                "landing.tariff.one-time-note"
                              )}
                            </div>
                          </div>
                        )}

                        {plan.locked &&
                          !!plan.lockedDesc && (
                            <div
                              className="title title__description"
                              style={{
                                marginTop: 12,
                                opacity: 0.75,
                                textAlign: "center",
                              }}
                            >
                              {plan.lockedDesc}
                            </div>
                          )}

                        <div className="services__selected-wrapper">
                          <div
                            className="services__selected"
                            style={{
                              display: selected
                                ? "block"
                                : "none",
                            }}
                          >
                            {t(
                              "landing.tariff.selected",
                              "Selected"
                            )}
                          </div>
                        </div>
                      </div>

                      {/* BACK SIDE */}
                      {!plan.locked && (
                        <div
                          className="services__tariff-face services__tariff-face--back"
                          style={{
                            ...faceBaseStyle,
                            transform: isFlipped
                              ? "rotateY(0deg)"
                              : "rotateY(-180deg)",
                            pointerEvents: isFlipped
                              ? "auto"
                              : "none",
                          }}
                        >
                          <div
                            className="text__price-set"
                            onClick={(event) =>
                              toggleFlip(
                                event,
                                plan.id
                              )
                            }
                            aria-label={t(
                              "cart.button.back",
                              "Back"
                            )}
                            title={t(
                              "cart.button.back",
                              "Back"
                            )}
                          >
                            {t(
                              "cart.button.back",
                              "Back"
                            )}
                          </div>

                          <h6 className="title title__prices title__prices--max">
                            {plan.oneTimeTitle}
                          </h6>

                          {!!plan.oneTimeDesc && (
                            <div className="title__block">
                              <div className="text__price-description">
                                {plan.oneTimeDesc}
                              </div>
                            </div>
                          )}

                          <div className="title__attention-block">
                            <div className="title__attention">
                              {t(
                                "landing.tariff.advance"
                              )}
                            </div>

                            <div className="title__attention-description">
                              {t(
                                "landing.tariff.advance.desc"
                              )}
                            </div>
                          </div>

                          <ul className="services__list">
                            {plan.oneTimeServices.map(
                              (service, index) => (
                                <li
                                  key={index}
                                  className="text text__prices"
                                >
                                  {service}
                                </li>
                              )
                            )}
                          </ul>

                          <div
                            className="text__price-description"
                            style={{
                              marginTop: 10,
                            }}
                          >
                            {t(
                              "landing.tariff.one-time-not-included"
                            )}
                          </div>

                          <div className="services__selected-wrapper">
                            <div
                              className="services__selected"
                              style={{
                                display: selected
                                  ? "block"
                                  : "none",
                              }}
                            >
                              {t(
                                "landing.tariff.selected",
                                "Selected"
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default memo(Price);